<template>
  <div class="checkout-screen">
    <div class="flex flex-col w-full">
      <CheckoutHeader current-screen="complete" />
      <VPLoadingSpinner :loading="summaryLoading">
        <main class="checkout-main">
          <div class="flex flex-col items-center w-full">
            <div class="text-vp-secondary-1-900 flex gap-4 bg-vp-blue-300 p-2 sm:p-4 my-2 sm:my-4 rounded items-start w-full">
              <template v-if="isCompleted">
                <div class="rotate-45">
                  <iconify-icon
                      icon="mdi:ticket-confirmation-outline"
                  />
                </div>
                <Transition>
                  <div
                      v-if="orderSummary?.customer"
                      class="text-sm"
                  >
                    <h1 class="text-base font-semibold">
                      {{ $gettext('Payment Successful') }}
                    </h1>
                    <p>{{ $gettext("You're all set, %{name}! 🎉", { name: orderSummary.customer.firstName }) }}</p>
                    <p>
                      {{ $gettext('Your tickets are in your inbox at %{email}.', { email: orderSummary.customer.email }) }}
                    </p>
                  </div>
                </Transition>
              </template>
              <template v-else>
                <h1 class="text-base font-semibold">
                  {{ $gettext('The order has not been completed!') }}
                </h1>
              </template>
            </div>

            <template v-if="isCompleted">
              <VPButton
                v-if="tokenValid && !emailForm"
                class="my-4 p-4 rounded-md min-w-full"
                theme="primary"
                variant="outline"
                @click="showEmailChange"
              >
                {{ $gettext('Need to update your email? Click here.') }}
                {{ $gettext('Time remaining: %{time}', { time: formattedRemaining }) }}
              </VPButton>

              <form
                v-if="tokenValid && emailForm"
                class="my-4 flex flex-col items-center w-full"
                @submit.prevent="onUpdateEmail"
              >
                <div class="my-2">
                  {{ $gettext('Time remaining: %{time}', { time: formattedRemaining }) }}
                </div>

                <div class="relative my-2 w-full sm:w-1/2 lg:w-1/3">
                  <input
                    id="email"
                    v-model="newEmail"
                    type="email"
                    :placeholder="$gettext('New email')"
                    autocomplete="email"
                    class="input-floating-label peer"
                  >
                  <label
                    for="email"
                    class="floating-label peer-placeholder-shown:top-1/2 peer-placeholder-shown:transform peer-placeholder-shown:-translate-y-1/2 peer-focus:top-3 peer-focus:text-sm peer-focus:text-vp-base-600 peer-focus:font-normal"
                  >
                    {{ $gettext('New email') }}
                  </label>
                </div>

                <VPButton type="submit">
                  {{ $gettext('Update email') }}
                </VPButton>
              </form>
            </template>
            <!--
            <VPButton
              class="p-4 rounded-md min-w-full"
              theme="primary"
              variant="outline"
              size="xl"
              @click="onNext"
            >{{ $gettext("My tickets") }}
            </VPButton>
          -->
            <VPButton
              component="a"
              :href="homeLink"
              class="p-4 rounded-md min-w-full"
              theme="primary"
              variant="outline"
              size="xl"
            >
              {{ $gettext('Get more tickets') }}
            </VPButton>
          </div>
        </main>
      </VPLoadingSpinner>
    </div>
    <section class="cart shopping-cart">
      <VPLoadingSpinner :loading="summaryLoading">
        <ShoppingCart
          v-if="isCompleted"
          read-only
          :order-summary="orderSummary"
          :event="orderSummary.event"
          :items="orderSummary.items"
          :price-breakdown="orderSummary.breakdown"
          :mobile-next-label="$gettext('Show')"
        />
      </VPLoadingSpinner>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useGettext } from 'vue3-gettext';
import VPButton from '@/components/form/VPButton.vue';
import ShoppingCart from '@/checkout/ShoppingCart.vue';
import { useCheckoutStore } from '@/stores/checkout';
import { LoadingState } from '@/util/LoadingState';
import { VPLoadingSpinner } from '@vp/vue-components';
import { Checkout } from '@vp/common';
import CheckoutHeader from '@/checkout/CheckoutHeader.vue';
import { usePageContext } from 'vike-vue/usePageContext';
import { DateTime, Duration } from 'luxon';
import { useToast } from 'vue-toastification';

type Props = {
  fingerprint?: string;
};

const props = defineProps<Props>();

const { $gettext } = useGettext();
const checkoutStore = useCheckoutStore();
const router = useRouter();
const pageContext = usePageContext();
const toast = useToast();

onMounted(() => {
  checkoutStore.loadOrderSummary(props.fingerprint);
  updateInterval.value = setInterval(updateRemaining, 1000);
});

onUnmounted(() => {
  clearInterval(updateInterval.value);
});

const summaryLoading = computed(() => checkoutStore.orderSummaryLoading != LoadingState.DONE);
const orderSummary = computed<Checkout.OrderSummary.OrderSummary | undefined>(() => checkoutStore.orderSummary);
const changeToken = computed(() => 'changeToken' in pageContext ? pageContext.changeToken : null);
const emailForm = ref(false);
const newEmail = ref('');

const tokenValid = computed(() => !!changeToken.value && changeTimeRemaining.value?.toMillis() > 0);
const tokenExpiresAt = computed(() => changeToken.value.expiresAt && DateTime.fromISO(changeToken.value.expiresAt));
const homeLink = computed(() => router.resolve({ name: 'OrderNew', params: { eventId: orderSummary.value?.event.id } }).href);

const changeTimeRemaining = ref<Duration>(Duration.fromMillis(0));
const updateInterval = ref<number>(0);

const isCompleted = computed(() => orderSummary.value?.state === 'completed');

const updateRemaining = () => {
  changeTimeRemaining.value = DateTime.now().until(tokenExpiresAt.value).toDuration(['minutes', 'seconds']);
  if (!changeTimeRemaining.value.isValid) {
    changeTimeRemaining.value = Duration.fromMillis(0);
    clearInterval(updateInterval.value);
  }
};

const formattedRemaining = computed(() => changeTimeRemaining.value.toFormat('m:ss'));

const showEmailChange = () => emailForm.value = true;

const onUpdateEmail = async () => {
  try {
    await checkoutStore.updateOrderEmail(props.fingerprint, changeToken.value?.token, newEmail.value);
    toast.success($gettext('Email updated successfully!'));
    clearInterval(updateInterval.value);
    pageContext.changeToken = null;
  } catch {
    toast.error($gettext('Failed to update email.'));
  }
};
</script>
